import type { ReactNode } from "react";

/** Bordered section container — a rounded box (the v2 card radius), no
 *  header of its own; every section title renders OUTSIDE the box via
 *  {@link LabeledSection}.
 *
 *  Pass `framed={false}` when the parent `HairlineGrid` already draws the
 *  cell borders — the panel becomes a plain background tile and the grid's
 *  hairlines sit flush against it. */
export function Panel({
  framed = true,
  className = "",
  children,
}: {
  framed?: boolean;
  /** Extra classes on the section wrapper — e.g. grid `order`/`col-span` when
   *  a Panel is a grid item that must lay out differently from source order. */
  className?: string;
  children: ReactNode;
}) {
  const outer = framed
    ? "rounded-xl border border-line bg-bg-muted"
    : "bg-bg-muted";
  return (
    <section className={`${outer} ${className}`.trim()}>
      <div className="p-5">{children}</div>
    </section>
  );
}

/**
 * A titled section whose mono eyebrow sits OUTSIDE / above its content — the
 * house standard for EVERY titled section, tables and lists included.
 * `framed` (default) wraps the body in the office bordered box; pass
 * `framed={false}` when the body brings its own framing (e.g. a `Table` or a
 * stat-card HairlineGrid). `actions` sit on the eyebrow row. One component
 * for the whole pattern — fix it here, fix it everywhere.
 */
export function LabeledSection({
  label,
  actions,
  framed = true,
  className = "",
  children,
}: {
  label: ReactNode;
  actions?: ReactNode;
  framed?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={className}>
      {actions ? (
        <div className="mb-4 flex items-center justify-between gap-3">
          <p className="label">{label}</p>
          {/* One group, so two links sit together at the end of the row
              instead of spreading across it. Zero height: a link or a pill
              is taller than the eyebrow, and given its height the row would
              grow around it, lowering the label and widening the gap to the
              body against every section beside it. Here it hangs over the
              row, centred on the label, and the row keeps the eyebrow's own
              height everywhere. */}
          <div className="flex h-0 items-center gap-3">{actions}</div>
        </div>
      ) : (
        <p className="label mb-4">{label}</p>
      )}
      {framed ? <Panel>{children}</Panel> : children}
    </section>
  );
}

/**
 * Masonry flow: panels pack into CSS columns by height instead of one tall
 * stack. Each panel is a direct child. `columns` is the responsive count
 * (e.g. "lg:columns-2 xl:columns-3"); one column below it.
 *
 * The gap between panels is padding, not margin: WebKit carries a margin that
 * lands on a column break over to the top of the next column, and that
 * column's first panel sat 24px below its neighbours'. Padding is inside the
 * panel's box, so it moves with the panel.
 *
 * The gap is split: 8px above each panel, 16px below, and the flow starts 8px
 * high to cancel the first row's. WebKit draws anything above a column's top
 * edge in the column before it, and a section's eyebrow link, centred on the
 * eyebrow and a few px taller, hangs just above the section. At the top of a
 * column iPad Safari drew it at the foot of the previous one; inside the
 * panel's own top padding it stays with its panel.
 */
export function Masonry({
  columns,
  children,
}: {
  columns: string;
  children: ReactNode;
}) {
  return (
    <div
      className={`-mt-2 columns-1 gap-6 ${columns} [&>*]:break-inside-avoid [&>*]:pt-2 [&>*]:pb-4`}
    >
      {children}
    </div>
  );
}
